<template>
  <div class="etx-container Container pv-0 bg-secondary">
    <div class="etx-center Center">
      <div class="etx-flex Flex gv-0 av-stretch">
        <div class="etx-flex__item Flex__item tab-l:c-7">
          <div
            class="FixedRatio ratio-3-2 falling-off-left falling-off-right tab-l:falling-off-right-none"
          >
            <ClientOnly>
              <div
                class="GoogleMaps"
                id="google-maps-single"
                ref="googleMapsRef"
              ></div>
            </ClientOnly>
          </div>
        </div>

        <div class="etx-flex__item Flex__item tab-s:c-10 tab-m:c-9 tab-l:c-5">
          <div class="etx-card Card">
            <div class="etx-card__item Card__item ph-0 pv-0 grow-1"></div>

            <div
              class="etx-card__item Card__item ph-0 tab-l:pl-150 pv-150 tab-l:pv-200"
            >
              <div class="etx-text Text" :class="entranceClassList">
                <h2
                  v-if="headingType == 'h2'"
                  class="font-h4 font-color-primary"
                >
                  {{ heading }}
                </h2>
                <h3
                  v-if="headingType == 'h3'"
                  class="font-h4 font-color-primary"
                >
                  {{ heading }}
                </h3>
                <h4
                  v-if="headingType == 'h4'"
                  class="font-h4 font-color-primary"
                >
                  {{ heading }}
                </h4>
                <HelperHtmlRenderer :htmlContent="text" />
              </div>

              <BlokkliProxyMode
                :isEditing="isEditing"
                fieldName="field_link_elements_rev"
                :elements="buttons"
              />
              <div
                class="etx-button-list ButtonList"
                :class="entranceClassList"
                v-if="buttons.length > 0"
              >
                <Button
                  v-for="button in buttons"
                  :type="button?.type"
                  :targetBlank="button?.targetBlank"
                  :href="button?.link?.domainUri?.path || button?.link?.uri?.path"
                  :label="button?.label"
                  :alt="button?.alt"
                />
              </div>
            </div>

            <div class="etx-card__item Card__item ph-0 pv-0 grow-1"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
const props = defineProps<{
  heading?: string
  headingType: string
  text?: string
  buttons: Array<any>
  parentId: string
}>()

const { options, isEditing } = defineBlokkli({
  bundle: 'location_route',
})
const entranceClassList = computed(() => {
  const classList = []
  if (!isEditing) {
    classList.push('will-appear', 'move-up')
  }
  return classList
})

// Data
const currentNode: string = props.parentId ?? '0'
const queryKey = `getLocation-${currentNode}`
const queryVars = {
  nid: [currentNode],
}

const { data: locationData } = await useAsyncData(queryKey, async () => {
  const result = await useGraphqlQuery('getLocation', queryVars)
  const items = result.data?.entityQuery?.items ?? []
  return items.length > 0
    ? {
        title: items[0].title,
        coordinates: items[0].coordinates,
      }
    : {}
})

// The Map
const googleMapsRef = ref(null)
const runtimeConfig = useRuntimeConfig()
const gMapApiKey = runtimeConfig.public.scripts.googleMaps.apiKey
let mapInstance = null
let markers = []
import customMapStyles from '~/assets/gmap/mapStyles.json'
import customMarkerIcon from '~/assets/symbols/location-pin.svg'

/// <reference types="google.maps" />
const { onLoaded: onGMapLoaded } = useScriptGoogleMaps({
  apiKey: gMapApiKey,
})

onMounted(() => {
  onGMapLoaded(async (instance) => {
    const maps = (await instance.maps) as any as typeof google.maps // upstream google type issue
    initGoogleMaps(maps)
  })
})

function initGoogleMaps(maps) {
  if (!googleMapsRef.value) return

  const mapOptions = {
    center: { lat: 46.7985, lng: 8.2318 }, // Center on Switzerland
    zoom: 10,
    disableDefaultUI: true,
    zoomControl: true,
    styles: customMapStyles,
    clickableIcons: false,
    minZoom: 7,
    // maxZoom: 15,
    restriction: {
      latLngBounds: {
        north: 49.0507, // 46.7985 + (250 / 111) ~ 51.1985
        south: 44.5462, // 46.7985 - (250 / 111) ~ 42.3985
        west: 0, // 8.2318 - (600 / (111 * cos(46.7985))) ~ 1.6318
        east: 14.8318, // 8.2318 + (600 / (111 * cos(46.7985))) ~ 14.8318
      },
    },
  }

  mapInstance = new maps.Map(googleMapsRef.value, mapOptions)

  drawMarkers(locationData.value)
}

function drawMarkers(location) {
  const marker = new google.maps.Marker({
    position: location.coordinates,
    map: mapInstance,
    title: location.title,
    icon: customMarkerIcon,
  })
  if (mapInstance) {
    mapInstance.setCenter(marker.getPosition())
  }
}
</script>
